import React, { useState, useImperativeHandle, forwardRef, ForwardRefRenderFunction } from 'react';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PasswordStrengthBar from 'react-password-strength-bar';

export interface PasswordFieldHandle {
  isValid: () => boolean;
}

export interface PasswordFieldProps {
  label?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string;
  className?: string;
  showStrength?: boolean;
  required?: boolean;
  disabled?: boolean;
}

const PasswordField: ForwardRefRenderFunction<PasswordFieldHandle, PasswordFieldProps> = ({
  label = 'Password',
  name,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  className,
  onFocus = null,
  showStrength = false,
  required = false,
  disabled = false
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [score, setScore] = useState(0);

  useImperativeHandle(ref, () => ({
    isValid: () => {
      // Only validate strength if showStrength is true
      return showStrength ? score >= 3 : true;
    },
  }), [score, showStrength]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

const autocompleteOffInputProps = showStrength
  ? {
      autocomplete: 'new-password',
      form: {
        autocomplete: 'off',
      },
    }
  : {};

    return (
      <>
        <TextField
          disabled={disabled}
          required={required}
          fullWidth
          type={showPassword ? 'text' : 'password'}
          label={label}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          error={error}
          helperText={error && helperText}
          margin="normal"
          className={className}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  disabled={disabled}
                  aria-label="toggle password visibility"
                  onClick={togglePasswordVisibility}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
            ...autocompleteOffInputProps,
          }}
        />
  
        {showStrength && (
          <PasswordStrengthBar
            password={value}
            className={className}
            minLength={8}
            scoreWords={['weak', 'weak', 'weak', 'good', 'strong']}
            shortScoreWord=""
            onChangeScore={setScore}
          />
        )}
      </>
    );
  };

export default forwardRef(PasswordField);
